import { isAxiosError } from 'axios';

import { InteractiveTable, LoadingButton, MoreButton } from '@ingrid/luterkort-react';

import { DetailsDrawer as Drawer } from '~src/components/DetailsDrawer/DetailsDrawer';
import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { IgnoredError } from '~src/errors/IgnoredError';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';
import {
  getStatusColor,
  getStatusLabel,
  isBookingFailedResponse,
  shouldShowStatusReason,
} from '~src/utils/status-helpers';

import { useShipmentActions } from '../hooks';
import { useShipmentBookParcelsMutation, useShipmentSchedulePickupMutation } from '../mutations';
import { useShipmentMetadataPolling } from '../polling';
import { ShipmentListItem } from '../queries';
import { Menu } from './menu';

interface Props {
  shipment?: ShipmentListItem;
}

function ShipmentDrawerAction({ shipment }: Props) {
  const { setHighlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  const schedulePickupMutation = useShipmentSchedulePickupMutation();
  const shipmentMetadataPolling = useShipmentMetadataPolling();
  const bookMutation = useShipmentBookParcelsMutation();
  const tracking = useTracking();

  const actions = useShipmentActions({
    shipment: { direction_type: shipment?.direction_type, status: shipment?.status },
  });

  const bookShipment = async () => {
    const id = shipment?.id;

    const promise = async () => {
      try {
        await bookMutation.mutateAsync({ shipment_id: id });
        tracking.capture(TrackingEventEnum.ShipmentBookingCompleted, { shipmentId: id });
      } catch (error) {
        const isRosettaMapped = isAxiosError(error) && error.response?.headers['x-ingrid-rosetta-mapped'] === 'true';
        tracking.capture(TrackingEventEnum.ShipmentBookingFailed, { shipmentId: id, isRosettaMapped });

        if (isBookingFailedResponse(error)) {
          await shipmentMetadataPolling.mutateAsync({
            pollUntil: data => data?.status === 'INVALID_BOOKING',
            variables: id,
          });

          setHighlightedKey(null);

          return Promise.reject(new IgnoredError('failed to book'));
        }

        throw error;
      }

      await shipmentMetadataPolling.mutateAsync({
        pollUntil: data => data?.status === 'BOOKED',
        variables: id,
      });

      setHighlightedKey(null);
    };

    await toast.promise(promise, {
      loading: { title: t('shipments.details.bookLoading') },
      error: { title: t('shipments.details.bookError') },
      success: { title: t('shipments.details.bookSuccess') },
    });
  };

  const schedulePickup = async () => {
    const promise = async () => {
      try {
        await schedulePickupMutation.mutateAsync({ id: shipment?.id });
        tracking.capture(TrackingEventEnum.ShipmentSchedulePickupCompleted, { shipmentId: shipment?.id });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        tracking.capture(TrackingEventEnum.ShipmentSchedulePickupFailed, { shipmentId: shipment?.id });

        return Promise.reject(new Error('failed to schedule pickup'));
      }

      await shipmentMetadataPolling.mutateAsync({
        variables: shipment?.id,
        pollUntil: data => data?.status === 'PICKUP_BOOKED' || data?.status === 'PICKUP_SCHEDULED',
      });

      setHighlightedKey(null);
    };

    await toast.promise(promise, {
      error: { title: t('shipments.details.bookPickupError') },
      loading: { title: t('shipments.details.bookPickupLoading') },
      success: { title: t('shipments.details.bookPickupSuccess') },
    });
  };

  if (!shipment) {
    return null;
  }

  return (
    <>
      {actions.includes('BOOK') && (
        <RoleGuard roles={['transport:edit']} withTooltip>
          <LoadingButton loading={bookMutation.isPending || shipmentMetadataPolling.isPending} onClick={bookShipment}>
            {t('common.book')}
          </LoadingButton>
        </RoleGuard>
      )}

      {actions.includes('SCHEDULE_PICKUP') && (
        <RoleGuard roles={['transport:edit']} withTooltip>
          <LoadingButton loading={schedulePickupMutation.isPending} onClick={schedulePickup}>
            {t('common.schedulePickup')}
          </LoadingButton>
        </RoleGuard>
      )}

      <Menu shipment={shipment} trigger={<MoreButton variant="secondary" />} hideShowDetails />
    </>
  );
}

export function DetailsDrawer({ shipment: s }: Props) {
  const { t } = useTranslation();

  return (
    <Drawer
      actions={<ShipmentDrawerAction shipment={s} />}
      detailsHref={s?.id}
      nodes={[
        <Drawer.Banner
          when={shouldShowStatusReason(s?.status, s?.status_message)}
          subtitle={s?.status_message}
          title={t('errors.generic')}
          variant="critical"
          key="message"
        />,
        <Drawer.Plain label="shipments.list.shipmentId" value={s?.id} key="sid" />,
        <Drawer.Direction label="common.direction" value={s?.direction_type} key="direction" />,
        <Drawer.DotBadge
          variant={s?.status ? getStatusColor(s.status) : undefined}
          text={s?.status ? t(getStatusLabel(s.status)) : undefined}
          label="common.status"
          key="status"
        />,
        <Drawer.SubRow label="shipments.list.trackingNumbers" value={s?.tracking_numbers} key="tracking" />,
        <Drawer.CarrierProduct label="common.carrierProduct" value={s?.shipping_method?.id} key="carrier" />,
        <Drawer.Address label="common.shipFrom" value={s?.address_from} key="from" />,
        <Drawer.Address label="common.shipTo" value={s?.address_to} key="to" />,
        <Drawer.Date label="shipments.list.shippingDate" value={s?.shipping_date} key="shipped" />,
        <Drawer.Date label="common.createdAt" value={s?.created_at} time key="created" />,
        <Drawer.Date label="common.updatedAt" value={s?.updated_at} time key="updated" />,
      ]}
    />
  );
}
